import {
  Flex,
  Input,
  NumberInput,
  Select,
  SimpleGrid,
  TextInput,
} from "@mantine/core";
import { isNotEmpty, matches, useForm } from "@mantine/form";
import get from "lodash/get";
import { useCallback, useEffect, useMemo } from "react";

import { yesNoOptions } from "../../constants.ts";
import {
  ClikCity,
  ClikCountry,
  ClikDataCategory,
  FileResult,
  LimitRequestType,
} from "../../graphql/generated.ts";
import { useCurrentUserContext } from "../../providers/CurrentUserProvider.tsx";
import {
  categoryOptions,
  ClikDataDetail,
} from "../../types/clikData/clikData.ts";
import { CustomerOption } from "../../types/customer.ts";
import {
  generateLimitName,
  limitRequestTypeOptions,
  tenorOptions,
} from "../../types/limitRequest/limitRequest.ts";
import {
  getClickCity,
  getClikCityOptions,
  getClikCountry,
  getClikCountryOptions,
} from "../../utils/clikData.ts";
import { getCompanyOptions } from "../../utils/company.ts";
import { parseStringToNumber, priceFormatter } from "../../utils/number.ts";
import { phoneMatcher } from "../../utils/phone.ts";
import { isBababosTenant } from "../../utils/user.ts";
import Dropzone from "../Dropzone/Dropzone.tsx";
import SectionTitle from "../Section/SectionTitle/SectionTitle.tsx";
import { ClikDataFormOut } from "./types.ts";

interface Props {
  showCategorySection?: boolean;
  value: ClikDataDetail | null;
  cities: ClikCity[] | null | undefined;
  countries: ClikCountry[] | null | undefined;
  companies: CustomerOption[] | null | undefined;
  onValueChange: (value: ClikDataFormOut) => void;
  onValidChange: (valid: boolean) => void;
}

const ClikDataInfoForm = ({
  showCategorySection = true,
  value,
  cities,
  countries,
  companies,
  onValueChange,
  onValidChange,
}: Props) => {
  const { user } = useCurrentUserContext();

  const checkHasGeneralLimit = useCallback(
    (
      companyId: number | undefined,
      limitRequestType: LimitRequestType | undefined
    ) => {
      const isGeneralLimit =
        limitRequestType === LimitRequestType.NewGeneralLimit;
      const company = companies?.find((c) => c.id === companyId);
      return isGeneralLimit && company?.hasGeneralLimit;
    },
    [companies]
  );

  const isLimitRequest = useCallback(
    (category: ClikDataCategory | undefined) => {
      return (
        category &&
        [
          ClikDataCategory.LimitRequest,
          ClikDataCategory.LimitRequestWithoutClik,
        ].includes(category)
      );
    },
    []
  );

  const form = useForm({
    initialValues: {
      company: value?.company,
      category:
        value?.category ??
        (isBababosTenant(user) ? undefined : ClikDataCategory.ClikRequest),
      bussinessIdentifyNumber: value?.companyData?.bussinessIdentifyNumber,
      phone: value?.companyData?.phone,
      npwpCompany: value?.companyData?.npwpCompany,
      npwpAddress: value?.companyData?.npwpAddress,
      subDistrict: value?.companyData?.subDistrict,
      district: value?.companyData?.district,
      city: value?.companyData?.city,
      postalCode: value?.companyData?.postalCode,
      country: value?.companyData?.country,
      limitRequestAmount: value?.limitRequest?.limitRequestAmount
        ? Number(value.limitRequest.limitRequestAmount)
        : 0,
      tenorRequest: value?.limitRequest?.tenorRequest
        ? String(value.limitRequest.tenorRequest)
        : undefined,
      suratPengajuanLimit: value?.limitRequest?.suratPengajuanLimit,
      formLOI: value?.limitRequest?.formLOI ?? value?.companyData?.formLOI,
      supportingDocument: value?.limitRequest?.supportingDocument,
      limitRequestType: value?.limitRequest?.limitRequestType,
      limitName: value?.limitRequest?.limitName,
      headOfRiskApproval: undefined,
      chiefCommitteeApproval: undefined,
    },
    validate: {
      company: isNotEmpty("Field is required"),
      category: isNotEmpty("Field is required"),
      bussinessIdentifyNumber: isNotEmpty("Field is required"),
      phone: phoneMatcher,
      npwpCompany: isNotEmpty("Field is required"),
      npwpAddress: isNotEmpty("Field is required"),
      subDistrict: isNotEmpty("Field is required"),
      district: isNotEmpty("Field is required"),
      city: isNotEmpty("Field is required"),
      postalCode: matches(
        /^\d{5}$/,
        "The postal code must consist of a maximum of 5 characters. It can include only numbers"
      ),
      country: isNotEmpty("Field is required"),
      limitRequestAmount: (value, values) =>
        isLimitRequest(values.category)
          ? isNotEmpty("Field is required")(value)
          : null,
      tenorRequest: (value, values) =>
        isLimitRequest(values.category)
          ? isNotEmpty("Field is required")(value)
          : null,
      suratPengajuanLimit: (value, values) =>
        isLimitRequest(values.category)
          ? isNotEmpty("Field is required")(value)
          : null,
      formLOI: (value, values) =>
        values.category !== ClikDataCategory.LimitRequestWithoutClik
          ? isNotEmpty("Field is required")(value)
          : null,
      limitRequestType: (value, values) => {
        if (!isLimitRequest(values.category)) {
          return null;
        }
        const hasGeneralLimit = checkHasGeneralLimit(
          get(values, "company.id"),
          value
        );
        if (hasGeneralLimit) {
          return "This customer already has General Limit, please choose “Top Up Limit” if you want to increase this customer limit";
        }
        return isNotEmpty("Field is required")(value);
      },
      limitName: (value, values) =>
        isLimitRequest(values.category)
          ? isNotEmpty("Field is required")(value)
          : null,
      headOfRiskApproval: (value, values) =>
        values.category === ClikDataCategory.LimitRequestWithoutClik
          ? isNotEmpty("Field is required")(value)
          : null,
      chiefCommitteeApproval: (value, values) =>
        values.category === ClikDataCategory.LimitRequestWithoutClik
          ? isNotEmpty("Field is required")(value)
          : null,
    },
    validateInputOnBlur: true,
  });

  const isFormValid = useMemo(() => {
    if (form.values.category === ClikDataCategory.LimitRequestWithoutClik) {
      return (
        form.isValid() &&
        form.values.headOfRiskApproval === "true" &&
        form.values.chiefCommitteeApproval === "true"
      );
    }
    return form.isValid();
  }, [form]);

  const handleFileUploaded = useCallback(
    (fieldName: string, files: FileResult[]) => {
      if (fieldName === "npwpCompany") {
        form.setValues({ npwpCompany: files[0] });
      }
      if (fieldName === "suratPengajuanLimit") {
        form.setValues({ suratPengajuanLimit: files[0] });
      }
      if (fieldName === "formLOI") {
        form.setValues({ formLOI: files[0] });
      }
      if (fieldName === "supportingDocument") {
        form.setValues({ supportingDocument: files[0] });
      }
    },
    [form]
  );

  const handleRemoveFile = useCallback(
    (fieldName: string) => {
      form.setFieldValue(fieldName, undefined);
    },
    [form]
  );

  const handleSetCompany = (companyId: string) => {
    const company = companies?.find((c) => c.id === Number(companyId));
    form.setValues({
      company: {
        id: Number(companyId),
        name: company?.name ?? "",
        salesName: company?.salesName ?? "",
        salesTeamName: company?.salesTeamName ?? "",
      },
    });
  };

  const handleSetClikCity = (cityId: string) => {
    const clikCity = getClickCity(cityId, cities);
    form.setValues({
      city: {
        id: Number(cityId),
        name: clikCity?.name ?? "",
        code: clikCity?.code ?? "",
      },
    });
  };

  const handleSetClikCountry = (countryId: string) => {
    const clikCountry = getClikCountry(countryId, countries);
    form.setValues({
      country: {
        id: Number(countryId),
        name: clikCountry?.name ?? "",
        code: clikCountry?.code ?? "",
      },
    });
  };

  const handleLimitRequestTypeChange = useCallback(
    (value: string) => {
      const limitRequestType = value as LimitRequestType;
      form.setValues({
        limitRequestType,
        limitName: generateLimitName(
          limitRequestType,
          form.values.company?.name
        ),
      });
    },
    [form]
  );

  const handleFormChange = useCallback(
    (values: typeof form.values) => {
      onValidChange(isFormValid);
      if (isFormValid) {
        onValueChange(values as ClikDataFormOut);
      }
    },
    [form, isFormValid, onValidChange, onValueChange]
  );

  const renderCategorySection = useCallback(() => {
    const formLoiInput = (
      <Input.Wrapper
        required={
          form.values.category !== ClikDataCategory.LimitRequestWithoutClik
        }
        label="Form LOI"
        size="m"
      >
        <Dropzone
          maxWidth="100%"
          value={form.values.formLOI ? [form.values.formLOI] : null}
          onUploadSuccess={(files) => handleFileUploaded("formLOI", files)}
          onRemove={() => handleRemoveFile("formLOI")}
        />
      </Input.Wrapper>
    );
    if (form.values.category === ClikDataCategory.ClikRequest) {
      return formLoiInput;
    }
    if (isLimitRequest(form.values.category)) {
      return (
        <>
          <NumberInput
            required
            precision={2}
            parser={parseStringToNumber}
            formatter={priceFormatter}
            min={0}
            hideControls
            label="Limit Request"
            placeholder="Enter Limit Request"
            size="m"
            {...form.getInputProps("limitRequestAmount")}
          />
          <Select
            required
            size="m"
            placeholder="Select Tenor Request"
            label="Tenor Request (days)"
            data={tenorOptions}
            {...form.getInputProps("tenorRequest")}
          />
          <Input.Wrapper required label="Surat Pengajuan Limit" size="m">
            <Dropzone
              maxWidth="100%"
              value={
                form.values.suratPengajuanLimit
                  ? [form.values.suratPengajuanLimit]
                  : null
              }
              onUploadSuccess={(files) =>
                handleFileUploaded("suratPengajuanLimit", files)
              }
              onRemove={() => handleRemoveFile("suratPengajuanLimit")}
            />
          </Input.Wrapper>
          {formLoiInput}
          <Select
            required
            size="m"
            placeholder="Select Limit Request Type"
            label="Limit Request Type"
            data={limitRequestTypeOptions}
            {...form.getInputProps("limitRequestType")}
            onChange={handleLimitRequestTypeChange}
          />
          <TextInput
            required
            label="Limit Name"
            placeholder="Enter Limit Name"
            size="m"
            {...form.getInputProps("limitName")}
            disabled={
              form.values.limitRequestType !== LimitRequestType.NewProjectLimit
            }
          />
          {form.values.category ===
            ClikDataCategory.LimitRequestWithoutClik && (
            <Input.Wrapper label="Supporting document" size="m">
              <Dropzone
                maxWidth="100%"
                value={
                  form.values.supportingDocument
                    ? [form.values.supportingDocument]
                    : null
                }
                onUploadSuccess={(files) =>
                  handleFileUploaded("supportingDocument", files)
                }
                onRemove={() => handleRemoveFile("supportingDocument")}
              />
            </Input.Wrapper>
          )}
        </>
      );
    }
  }, [
    form,
    handleFileUploaded,
    handleLimitRequestTypeChange,
    handleRemoveFile,
    isLimitRequest,
  ]);

  useEffect(() => {
    handleFormChange(form.values);
  }, [form.values, handleFormChange]);

  return (
    <SimpleGrid
      cols={3}
      breakpoints={[{ maxWidth: "sm", cols: 1 }]}
      spacing={20}
      verticalSpacing={16}
    >
      <Flex direction="column" gap={12}>
        <SectionTitle flex={0} isSmall>
          Company Info
        </SectionTitle>
        <Select
          required
          size="m"
          placeholder="Select Company Name"
          label="Company Name"
          disabled={!!value?.id}
          data={getCompanyOptions(companies)}
          {...form.getInputProps("company")}
          value={form.values.company?.id?.toString()}
          searchable
          filterDataOnExactSearchMatch
          onChange={handleSetCompany}
        />
        <TextInput
          disabled
          label="Sales Name"
          placeholder="Sales Name"
          size="m"
          value={form.values.company?.salesName ?? ""}
        />
        <TextInput
          disabled
          label="Sales Team"
          placeholder="Sales Team"
          size="m"
          value={form.values.company?.salesTeamName ?? ""}
        />
        <TextInput
          required
          label="Business Identity Number"
          placeholder="Enter Business Identity Number"
          size="m"
          {...form.getInputProps("bussinessIdentifyNumber")}
        />
        <TextInput
          required
          type="phone"
          label="Phone Number"
          placeholder="Enter Phone Number"
          size="m"
          {...form.getInputProps("phone")}
        />
        <Input.Wrapper required label="NPWP Company" size="m">
          <Dropzone
            maxWidth="100%"
            value={form.values.npwpCompany ? [form.values.npwpCompany] : null}
            onUploadSuccess={(files) =>
              handleFileUploaded("npwpCompany", files)
            }
            onRemove={() => handleRemoveFile("npwpCompany")}
          />
        </Input.Wrapper>
      </Flex>
      <Flex direction="column" gap={12}>
        <SectionTitle flex={0} isSmall>
          Company Address
        </SectionTitle>
        <TextInput
          required
          type="text"
          label="NPWP Address"
          placeholder="Enter NPWP Address"
          size="m"
          {...form.getInputProps("npwpAddress")}
        />
        <TextInput
          required
          label="Sub-district"
          placeholder="Enter Sub-district"
          size="m"
          {...form.getInputProps("subDistrict")}
        />
        <TextInput
          required
          label="District"
          placeholder="Enter District"
          size="m"
          {...form.getInputProps("district")}
        />
        <Select
          required
          size="m"
          placeholder="Salect City"
          label="City"
          data={getClikCityOptions(cities)}
          {...form.getInputProps("city")}
          value={form.values.city?.id.toString()}
          searchable
          filterDataOnExactSearchMatch
          onChange={handleSetClikCity}
        />
        <TextInput
          required
          type="number"
          label="Postal Code"
          placeholder="Postal Code"
          size="m"
          {...form.getInputProps("postalCode")}
        />
        <Select
          required
          size="m"
          placeholder="Select Country"
          label="Country"
          data={getClikCountryOptions(countries)}
          {...form.getInputProps("country")}
          value={form.values.country?.id.toString()}
          searchable
          filterDataOnExactSearchMatch
          onChange={handleSetClikCountry}
        />
      </Flex>
      {showCategorySection && (
        <Flex direction="column" gap={12}>
          <SectionTitle flex={0} isSmall>
            {isBababosTenant(user) ? "Category" : "Additional Info"}
          </SectionTitle>
          {isBababosTenant(user) && (
            <Select
              required
              size="m"
              placeholder="Select Category"
              label="Category"
              data={categoryOptions}
              {...form.getInputProps("category")}
            />
          )}
          {renderCategorySection()}
          {form.values.category ===
            ClikDataCategory.LimitRequestWithoutClik && (
            <>
              <Select
                withinPortal
                dropdownPosition="top"
                required
                data={yesNoOptions}
                label="Head of Risk Approval"
                placeholder="Select Head of Risk Approval"
                size="m"
                {...form.getInputProps("headOfRiskApproval")}
              />
              <Select
                withinPortal
                dropdownPosition="top"
                required
                data={yesNoOptions}
                label="Chief / Committee Approval"
                placeholder="Select Chief / Committee Approval"
                size="m"
                {...form.getInputProps("chiefCommitteeApproval")}
              />
            </>
          )}
        </Flex>
      )}
    </SimpleGrid>
  );
};

export default ClikDataInfoForm;
